/**
 * Element Schema Validation Utilities
 * Provides validation functions for element schemas and values
 */

import { sanitizeElementValue } from './element-schema-sanitizers'
import { createTypeValidator, ElementSchemaSchema, SectionElementSchemaSchema } from './element-schema-validators'

import type {
  ElementSchema,
  ElementValidationResult,
  ExtractedElementValue,
  SectionElementSchema,
} from '@/types/element-schema.type'

/**
 * Validate an element schema definition
 */
export function validateElementSchema(schema: unknown): ElementValidationResult {
  const result = ElementSchemaSchema.safeParse(schema)
  if (result.success) {
    return { isValid: true, errors: [] }
  }
  return {
    isValid: false,
    errors: result.error.errors.map(err => ({
      key: err.path.join('.') || 'root',
      message: err.message,
    })),
  }
}

/**
 * Validate a section element schema definition
 */
export function validateSectionSchema(schema: unknown): ElementValidationResult {
  const result = SectionElementSchemaSchema.safeParse(schema)
  if (result.success) {
    return { isValid: true, errors: [] }
  }
  return {
    isValid: false,
    errors: result.error.errors.map(err => ({
      key: err.path.join('.') || 'root',
      message: err.message,
    })),
  }
}

/**
 * Validate a value against an element schema
 */
export function validateElementValue(value: unknown, schema: ElementSchema): ExtractedElementValue {
  const errors: string[] = []

  // Check required
  if (schema.required && (value === undefined || value === null || value === '')) {
    errors.push(`${schema.label} is required`)
    return {
      key: schema.key,
      type: schema.type,
      value,
      isValid: false,
      errors,
    }
  }

  // Skip validation for optional empty values
  if (value === undefined || value === null || value === '') {
    return {
      key: schema.key,
      type: schema.type,
      value: schema.default ?? value,
      isValid: true,
    }
  }

  // Sanitize value
  const sanitizedValue = sanitizeElementValue(value, schema.type)

  // Get type validator
  let typeValidator = createTypeValidator(schema.type, schema)

  // Add string constraints
  if (schema.type === 'text' || schema.type === 'textarea') {
    let stringValidator = typeValidator as unknown as import('zod').ZodString
    if (schema.minLength !== undefined) stringValidator = stringValidator.min(schema.minLength)
    if (schema.maxLength !== undefined) stringValidator = stringValidator.max(schema.maxLength)
    typeValidator = stringValidator as unknown as import('zod').ZodType
  }

  // Add number constraints
  if (schema.type === 'number') {
    let numberValidator = typeValidator as unknown as import('zod').ZodNumber
    if (schema.min !== undefined) numberValidator = numberValidator.min(schema.min)
    if (schema.max !== undefined) numberValidator = numberValidator.max(schema.max)
    typeValidator = numberValidator as unknown as import('zod').ZodType
  }

  const result = typeValidator.safeParse(sanitizedValue)

  if (!result.success) {
    errors.push(...result.error.errors.map((e: { message: string }) => e.message))
  }

  return {
    key: schema.key,
    type: schema.type,
    value: result.success ? result.data : sanitizedValue,
    isValid: result.success,
    errors: errors.length > 0 ? errors : undefined,
  }
}

/**
 * Validate all values against a section schema
 */
export function validateSectionValues(
  values: Record<string, unknown>,
  schema: SectionElementSchema
): ExtractedElementValue[] {
  return schema.elements.map(element => validateElementValue(values[element.key], element))
}

/**
 * Check if all extracted values are valid
 */
export function areAllValuesValid(extractedValues: ExtractedElementValue[]): boolean {
  return extractedValues.every(ev => ev.isValid)
}

/**
 * Get validation errors from extracted values
 */
export function getValidationErrors(
  extractedValues: ExtractedElementValue[]
): Array<{ key: string; errors: string[] }> {
  return extractedValues
    .filter((ev): ev is ExtractedElementValue & { errors: string[] } => !ev.isValid && Array.isArray(ev.errors))
    .map(ev => ({ key: ev.key, errors: ev.errors }))
}
